// VoiceTextarea — drop-in replacement for <textarea> with built-in Mic-Button + reactive Waveform.
// Apple-Style: minimalistisches SF-Symbols-artiges Mikrofon, Pulse-Animation beim Start/Stopp,
// türkise Waveform die auf Audio-Level reagiert.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Microphone, MicrophoneSlash, X } from '@phosphor-icons/react';
import useSpeechToText from '../lib/useSpeechToText';

const Waveform = ({ level }) => {
  // 5 reactive bars — base height + audio-level proportional growth
  const bars = [0.4, 0.7, 1.0, 0.7, 0.4];
  return (
    <div aria-hidden="true" className="flex items-center gap-[3px]" style={{ height: 18 }}>
      {bars.map((coef, i) => {
        const h = 4 + Math.min(14, level * 32 * coef + Math.sin(Date.now() / 140 + i) * 2);
        return (
          <motion.span
            key={i}
            animate={{ height: h }}
            transition={{ type: 'spring', damping: 22, stiffness: 380 }}
            style={{
              width: 3,
              borderRadius: 9999,
              background: 'var(--accent-cyan)',
              boxShadow: level > 0.04 ? '0 0 6px rgba(91,184,196,0.6)' : 'none',
            }}
          />
        );
      })}
    </div>
  );
};

const VoiceTextarea = ({
  value,
  onChange,
  placeholder = 'Schreib oder sprich — wir hören zu.',
  rows = 4,
  testId,
  language = 'de-DE',
  className = '',
  style = {},
  autoFocus = false,
  disabled = false,
}) => {
  // anchor = current text at start of recording, transcripts get appended afterwards
  const [anchor, setAnchor] = useState('');
  const taRef = useRef(null);

  const handleResult = useCallback(({ combined }) => {
    if (!combined) return;
    const sep = anchor && !anchor.endsWith(' ') && !anchor.endsWith('\n') ? ' ' : '';
    const next = (anchor + sep + combined).trimStart();
    onChange({ target: { value: next } });
  }, [anchor, onChange]);

  const { supported, listening, audioLevel, error, start, stop } = useSpeechToText({
    language,
    onResult: handleResult,
  });

  const toggle = () => {
    if (listening) {
      stop();
      if (navigator.vibrate) navigator.vibrate(8);
    } else {
      setAnchor(value || '');
      start();
    }
  };

  // Stop recording on unmount
  useEffect(() => () => stop(), [stop]);

  return (
    <div className={`relative ${className}`} style={style}>
      <textarea
        ref={taRef}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        autoFocus={autoFocus}
        disabled={disabled}
        className="w-full p-3 pr-14 text-sm"
        style={{
          background: 'var(--bg-base)',
          border: `1px solid ${listening ? 'var(--accent-cyan)' : 'var(--border-default)'}`,
          borderRadius: '12px',
          resize: 'vertical',
          outline: 'none',
          color: 'var(--text-primary)',
          lineHeight: 1.55,
          transition: 'border-color 0.25s ease',
          minHeight: 80,
        }}
        data-testid={testId}
      />

      {/* Mic Button */}
      {supported && (
        <motion.button
          type="button"
          onClick={toggle}
          whileTap={{ scale: 0.92 }}
          whileHover={{ scale: 1.04 }}
          aria-label={listening ? 'Aufnahme stoppen' : 'Sprachaufnahme starten'}
          className="absolute"
          style={{
            top: 8,
            right: 8,
            width: 36,
            height: 36,
            borderRadius: 9999,
            background: listening ? 'var(--accent-cyan)' : 'rgba(91,184,196,0.1)',
            border: `1px solid ${listening ? 'var(--accent-cyan)' : 'rgba(91,184,196,0.35)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            boxShadow: listening ? '0 0 0 4px rgba(91,184,196,0.18)' : 'none',
            transition: 'background 0.2s ease, box-shadow 0.2s ease',
          }}
          data-testid={testId ? `${testId}-mic` : 'voice-mic-btn'}
        >
          {listening
            ? <MicrophoneSlash size={18} weight="fill" color="#fff" />
            : <Microphone size={18} weight="duotone" color="var(--accent-cyan)" />}
          {/* Pulsing ring when listening */}
          {listening && (
            <motion.span
              initial={{ scale: 1, opacity: 0.6 }}
              animate={{ scale: 1.6, opacity: 0 }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'easeOut' }}
              style={{
                position: 'absolute',
                inset: -2,
                borderRadius: 9999,
                border: '2px solid var(--accent-cyan)',
                pointerEvents: 'none',
              }}
              aria-hidden="true"
            />
          )}
        </motion.button>
      )}

      {/* Active recording overlay: waveform pinned bottom-left of textarea */}
      <AnimatePresence>
        {listening && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.2 }}
            className="absolute flex items-center gap-2 px-3 py-1.5"
            style={{
              bottom: 10,
              left: 10,
              background: 'var(--bg-surface)',
              border: '1px solid rgba(91,184,196,0.5)',
              borderRadius: 9999,
              boxShadow: '0 4px 14px rgba(91,184,196,0.18)',
            }}
            data-testid={testId ? `${testId}-waveform` : 'voice-waveform'}
          >
            <Waveform level={audioLevel} />
            <span className="text-xs font-medium" style={{ color: 'var(--accent-cyan)' }}>
              höre zu…
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error toast */}
      <AnimatePresence>
        {error && !listening && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-2 flex items-center gap-2 px-3 py-1.5 text-xs"
            style={{
              background: 'rgba(220,38,38,0.08)',
              border: '1px solid rgba(220,38,38,0.3)',
              color: '#991B1B',
              borderRadius: 8,
            }}
            data-testid="voice-error"
          >
            <X size={12} /> {error}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VoiceTextarea;
